import React, { useEffect, useRef, useState } from "react";
import "./signup.css";
import { Labelbox } from "../../components";
import { useNavigate } from "react-router-dom";
import { CLIENT_ACTION } from "../../redux/action";
import ValidationLibrary from "../../utils/validationfunction";
import {
  encryptPassword,
  Toaster,
  dropdownName,
} from "../../utils/common";
import { buildSignupEmployeeAssignment } from "../../utils/clientSignupRouting";
import { Link } from "react-router-dom";
import signupBanner from "../../images/LoginImg.png";
import PhoneInput from "../../components/PhoneInput";

const SIGNUP_FIELD_ORDER = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "client_type",
  "consultant",
  "password",
  "confirmPassword",
];

const normalizeClientTypeForSave = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "business") return "Business";
  if (normalized === "individual") return "Individual";
  return String(value ?? "").trim();
};

const SignupPage = () => {
  const [inputs, setInputs] = useState({
    first_name: {
      type: "text",
      labelname: "First Name",
      value: "",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
    last_name: {
      type: "text",
      labelname: "Last Name",
      value: "",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
    email: {
      type: "text",
      labelname: "Email",
      value: "",
      validation: [{ name: "required" }, { name: "email" }],
      error: null,
      errmsg: null,
    },
    phone: {
      type: "text",
      labelname: "Phone Number",
      value: "",
      validation: [{ name: "mobile" }],
      error: null,
      errmsg: null,
    },
    client_type: {
      type: "select",
      labelname: "Client Type",
      value: "",
      dropdown: [
        { id: "Individual", value: "Individual" },
        { id: "Business", value: "Business" },
      ],
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
    consultant: {
      type: "select",
      labelname: "Consultant",
      value: "",
      dropdown: [],
      validation: [],
      error: null,
      errmsg: null,
    },
    password: {
      type: "text",
      labelname: "Password",
      value: "",
      validation: [
        { name: "required" },
        { name: "minLength", value: 8 },
        {
          name: "pattern",
          regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
          errorMsg:
            "Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.",
        },
      ],
      error: null,
      errmsg: null,
      password: true,
    },
    confirmPassword: {
      type: "text",
      labelname: "Confirm Password",
      value: "",
      validation: [
        { name: "required" },
        { name: "comparePassword", compareText: "" },
      ],
      error: null,
      errmsg: null,
      password: true,
    },
  });
  const [consultantOptions, setConsultantOptions] = useState([]);
  const [consultantSearch, setConsultantSearch] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const consultantByIdRef = useRef({});

  const navigate = useNavigate();

  useEffect(() => {
    const query = consultantSearch.trim();
    if (query.length < 2) {
      setConsultantOptions([]);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const employees = await CLIENT_ACTION.searchSignupConsultants(query);
      if (cancelled) return;

      const nextMap = {};
      const options = (employees || []).map((employee) => {
        nextMap[employee.employee_id] = employee;
        return {
          id: employee.employee_id,
          value: dropdownName(employee),
        };
      });

      consultantByIdRef.current = {
        ...consultantByIdRef.current,
        ...nextMap,
      };
      setConsultantOptions(options);
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [consultantSearch]);

  const handleChange = (value, fieldKey) => {
    const nextInputs = { ...inputs };
    if (fieldKey === "confirmPassword") {
      nextInputs[fieldKey].validation[1].compareText = inputs.password.value;
    }

    const errorcheck = ValidationLibrary.checkValidation(
      value,
      nextInputs[fieldKey].validation || [],
    );
    nextInputs[fieldKey].value = value;
    nextInputs[fieldKey].error = !errorcheck.state;
    nextInputs[fieldKey].errmsg = errorcheck.msg;
    setInputs(nextInputs);
  };

  const handleConsultantSearch = (searchText) => {
    setConsultantSearch(searchText);
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      const nextInputs = { ...inputs };
      const errorList = SIGNUP_FIELD_ORDER.filter((fieldKey) => {
        if (fieldKey === "consultant") return false;

        const errorcheck = ValidationLibrary.checkValidation(
          nextInputs[fieldKey].value,
          nextInputs[fieldKey].validation || [],
        );
        nextInputs[fieldKey].error = !errorcheck.state;
        nextInputs[fieldKey].errmsg = errorcheck.msg;
        return !errorcheck.state;
      });
      setInputs(nextInputs);

      if (!errorList.length) {
        const consultantId = nextInputs.consultant.value;
        const consultant = consultantId
          ? consultantByIdRef.current[consultantId]
          : null;
        const clientType = normalizeClientTypeForSave(
          nextInputs.client_type.value,
        );
        const employeeAssignment = buildSignupEmployeeAssignment({
          clientType,
          consultantId,
        });

        const result = await CLIENT_ACTION.insertUpdateClient({
          params: {
            first_name: nextInputs.first_name.value,
            last_name: nextInputs.last_name.value,
            email: nextInputs.email.value,
            phone: nextInputs.phone.value,
            client_type: clientType,
            password: encryptPassword(nextInputs.password.value),
            created_origin: 0,
            status: 1,
            Activated: true,
            sms_consent: smsConsent,
            ...employeeAssignment,
            sales: consultant?.manager_id ?? null,
          },
          insertUpdateFlag: 0,
        });
        if (result.status === 200 && result.data.status === 1) {
          Toaster.success("Your account has been created successfully");
          navigate("/");
        } else {
          Toaster.error(result.data.message);
        }
      }
    } catch (error) {
      Toaster.error("Unable to create your account. Please try again.");
    }
  };

  return (
    <div className="login-page">
      <div className="login-wrapper">
        {/* LEFT IMAGE */}
        <div className="login-image">
          <img src={signupBanner} alt="Signup banner" />
          <div className="image-overlay">
            <h2 className="hero-heading">
              <span className="heading-line"></span>
              Empowering Professionals,
              <br />
              Startups, and Business Owners
            </h2>
            <p>
              Smart tax strategy, business formation, bookkeeping, and payroll—
              <br></br>all in one place.
            </p>
          </div>
        </div>

        {/* RIGHT FORM */}
        <div className="login-form">
          <div className="form-header">
            <img src="/resources/images/Finlotax.png" alt="Finlotax Logo" />
            <h3>Create your account</h3>
          </div>

          <form style={{ marginTop: 10 }}>
            {SIGNUP_FIELD_ORDER.map((key) => (
              <div className="signup-field" key={key}>
                {key === "phone" ? (
                  <PhoneInput
                    value={inputs[key].value}
                    onChange={(val) => handleChange(val, key)}
                    labelname={inputs[key].labelname}
                    error={inputs[key].error}
                    errmsg={inputs[key].errmsg}
                  />
                ) : (
                  <Labelbox
                    type={inputs[key].type}
                    labelname={
                      key === "client_type" ? (
                        <span>
                          Client Type <span style={{ color: "red" }}>*</span>
                        </span>
                      ) : (
                        inputs[key].labelname
                      )
                    }
                    value={inputs[key].value}
                    changeData={(val) => handleChange(val, key)}
                    error={inputs[key].error}
                    errmsg={inputs[key].errmsg}
                    password={inputs[key].password}
                    dropdown={
                      key === "consultant"
                        ? consultantOptions
                        : inputs[key].dropdown
                    }
                    onSelectSearchChange={
                      key === "consultant" ? handleConsultantSearch : undefined
                    }
                    selectSearchPlaceholder={
                      key === "consultant"
                        ? "Search by name..."
                        : undefined
                    }
                  />
                )}
                {key === "consultant" && (
                  <p className="signup-consultant-hint">
                    Search by associate name. Leave blank if you are not
                    working with a specific associate yet.
                  </p>
                )}
              </div>
            ))}

            <label className="signup-sms-consent">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
              />
              <span>
                By checking this box, I agree to receive SMS text messages from
                FinloTax regarding my inquiry, account updates, and customer
                support at the mobile number provided. Message frequency varies.
                Message and data rates may apply. Reply STOP to opt out and HELP
                for help. View our{" "}
                <a
                  href="https://finlotax.com/privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://finlotax.com/terms-and-conditions/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms and Conditions
                </a>
                .
              </span>
            </label>

            <button
              style={{ marginTop: 10 }}
              className="submit-btn"
              onClick={handleSubmit}
            >
              Sign Up
            </button>
          </form>

          <p className="signup-text">
            Already have an account? <Link to="/login">Sign In</Link>
          </p>
          <p className="policy-links">
            <a
              href="https://finlotax.com/terms-and-conditions/"
              target="_blank"
              rel="noreferrer"
            >
              Term of Use
            </a>{" "}
            |{" "}
            <a
              href="https://finlotax.com/privacy-policy/"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>{" "}
            |{" "}
            <a
              href="https://finlotax.com/disclaimer/"
              target="_blank"
              rel="noreferrer"
            >
              Disclaimer
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
